<template>
    <div id="app">
        <Loading />
        <div v-if="loaded" ref="main" id="main">
            <div class="simple-layout">
                <div class="live-area">
                    <TeacherPlayer />
                    <div v-if="showStartBtn" class="start-btn" @click="handleClassStart">上课</div>
                    <div v-if="!isClassStarted" class="live-poster">
                        <div class="poster-bg" :style="posterBgStyle" />
                        <div class="poster-info">
                            <div class="poster-title">直播未开始</div>
                            <div v-if="liveStartTime" class="poster-time">开播时间：{{ liveStartTime }}</div>
                            <div v-if="countdownText" class="poster-countdown">距离开播还有 {{ countdownText }}</div>
                        </div>
                    </div>
                </div>
                <div class="chat-area">
                    <div class="chat-tabs">
                        <div
                            class="chat-tab"
                            :class="{ active: activeTab === 'chat' }"
                            @click="activeTab = 'chat'"
                        >聊天</div>
                        <div
                            class="chat-tab"
                            :class="{ active: activeTab === 'user' }"
                            @click="activeTab = 'user'"
                        >学员</div>
                    </div>
                    <div class="chat-tab-content">
                        <div v-show="activeTab === 'chat'" class="message-panel">
                            <MessageList />
                        </div>
                        <div v-show="activeTab === 'user'" class="user-list-panel">
                            <UserList />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div id="survey-container" v-if="surveyUrl">
            <iframe ref="iframe" :src="surveyUrl" />
        </div>
    </div>
</template>

<script>
import Loading from './components/Loading.vue'
import TeacherPlayer from './components/TeacherPlayer'
import MessageList from './components/InteractionPanel/MessageList'
import UserList from './components/InteractionPanel/UserList'
import language from './language/main.js'

const eventEmitter = BJY.eventEmitter
const auth = BJY.auth
const store = BJY.store
const config = BJY.config

export default {
    name: 'app',
    components: {
        Loading,
        TeacherPlayer,
        MessageList,
        UserList
    },
    props: {
        roomOptions: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loaded: false,
            webrtc: 1,
            showStartBtn: false,
            surveyUrl: '',
            activeTab: 'chat',
            originalViewport: '',
            isClassStarted: false,
            liveStartTime: '',
            liveThumb: '',
            countdownTimer: null,
            countdownText: ''
        }
    },
    computed: {
        posterBgStyle() {
            return this.liveThumb ? {
                backgroundImage: `url(${this.liveThumb})`,
                backgroundSize: 'contain'
            } : {}
        }
    },
    watch: {
        roomOptions: {
            immediate: true,
            deep: true,
            handler(val) {
                if (val) {
                    this.initPosterInfo()
                }
            }
        }
    },
    mounted() {
        this.lockViewport()
        this.bindZoomPrevent()
        if (this.webrtc && this.$Dialog && typeof this.$Dialog.show === 'function') {
            this.$Dialog
                .show({
                    title: '提示',
                    content: '正在进入教室。。。',
                    confirmText: '确认'
                })
                .then(() => {
                    console.log('教室内首次交互完成')
                })
                .catch((e) => {
                    console.log(e)
                })
        }

        BJY.WebrtcDialog.create()
        BJY.PhoneVerification.create()
        BJY.UserNoSupport.create()
    },
    created() {
        let $body = $('body')

        store.watch(
            'class.started',
            (started) => {
                this.isClassStarted = !!started
                this.showStartBtn = auth.isTeacher() && !started
                if (started) {
                    this.clearCountdown()
                }
            },
            true
        )

        // 读取并解析开播时间与封面图（兼容多个字段名）
        this.initPosterInfo()

        eventEmitter.one(eventEmitter.CLASSROOM_CONNECT_TRIGGER, (event, data) => {
            var extension = BJY.getExtension()
            // Safari 对 WebRTC 支持不完整，强制使用 HTML/FLV 播放
            var isSafari = BJY.browser.safari || BJY.browser.safariWebview || (/safari/i.test(navigator.userAgent) && !/chrome|crios|crmo/i.test(navigator.userAgent))
            if (BJY.browser.wechat || isSafari) {
                extension = BJY.Player.html
            }
            if (extension === BJY.Player.html) {
                extension.extensions.useFlv = true
            }
            extension.init().then(() => {
                eventEmitter.trigger(eventEmitter.CLASSROOM_CONNECT_TRIGGER, data)
            })
            return false
        })
        eventEmitter
            .on(eventEmitter.CLASSROOM_CONNECT_FAIL, () => {
                alert(language.ROOM_SERVER_CONNECT_FAIL)
            })
            .on(eventEmitter.LOGIN_CONFLICT, () => {
                alert(language.LOGIN_CONFLICT)
            })
            .one(eventEmitter.VIEW_RENDER_TRIGGER, () => {
                this.loaded = true

                if (auth.isTeacher() || auth.isAssistant()) {
                    store.set('class.speakState', config.SPEAK_STATE_FREE)
                } else {
                    store.set('class.speakState', config.SPEAK_STATE_LIMIT)
                }

                if (auth.isPresenter() && store.get('class.started')) {
                    setTimeout(() => {
                        var player = BJY.Player.instances[BJY.store.get('user.id')]
                        BJY.userPublish.setDevice(player, !player.videoOn, !player.audioOn)
                    }, 1000)
                }

                if (auth.isStudent()) {
                    $body.addClass('student')
                }
            })
            .one(eventEmitter.LOADING_PANEL_END, () => {
                $('#main').css('opacity', 1)
            })
            .on(eventEmitter.CLASS_END, () => {
                eventEmitter.trigger(eventEmitter.MEDIA_SWITCH_TRIGGER, {
                    audioOn: false,
                    videoOn: false
                })
            })
            .on(eventEmitter.CLASS_START, () => {
                if (auth.isPresenter()) {
                    setTimeout(() => {
                        var player = BJY.Player.instances[BJY.store.get('user.id')]
                        BJY.userPublish.setDevice(player, !player.videoOn, !player.audioOn)
                    }, 1000)
                }
            })
            .on(eventEmitter.CLASSROOM_CONNECT_SUCCESS, (event, data) => {
                void event
                void data
            })
            .on(eventEmitter.COMMAND_RECEIVE, (event, data) => {
                if (data.command && data.command.command_type === 'logout' && data.toId === store.get('user.id')) {
                    alert('你已被踢出教室')
                    location.reload()
                }
            })
            .on(eventEmitter.INTERFACE_FETCH_SUCCESS, () => {
                let isSdk = true
                let noRedirect = true
                if (typeof BJY.checkQuestionnaireAndTurn !== 'function') {
                    return
                }
                BJY.checkQuestionnaireAndTurn(
                    store.get('class.id'),
                    store.get('token'),
                    store.get('user.type'),
                    store.get('user.number'),
                    isSdk,
                    noRedirect
                ).then((data) => {
                    if (data && data.isNeedFill) {
                        this.surveyUrl = data.url
                        let me = this
                        window.onmessage = function (e) {
                            console.log('onmessage', e.data)
                            let message = e.data
                            if (message.type === 'surveyClose' && message.isSubmit) {
                                me.surveyUrl = ''
                            }
                        }
                    }
                })
            })

        this.init()
        this.$nextTick(() => {
            this.bindFullscreenState()
        })
    },
    methods: {
        init() {
            console.log('******************************************************************************')
            console.log('*   欢迎使用 BJY JS SDK - 百家云直播 - ' + BJY.version)
            console.log('*   API 文档：https://www.baijiayun.com/js-sdk/doc/#/README')
            console.log('*   版本更新日志：https://www.baijiayun.com/js-sdk/doc/#/change-log')
            console.log('*   常见问题：https://www.baijiayun.com/js-sdk/doc/#/live?id=%e5%b8%b8%e8%a7%81%e9%97%ae%e9%a2%98')
            console.log('*   注意事项：自己创建教室测试时，注意非webrtc类型教室传入webrtc: 0')
            console.log('*   sign签名规则：https://dev.baijiayun.com/wiki/detail/1')
            console.log('******************************************************************************')

            var options = this.normalizeRoomOptions(this.roomOptions)
            if (!options.prefixName || !options.room_id || !options.sign) {
                this.$emit('error', '缺少百家云直播初始化参数：prefixName、room_id 或 sign')
                return
            }

            var classOption = {
                id: String(options.room_id),
                speakState: BJY.config.SPEAK_STATE_LIMIT
            }
            if (Number(options.webrtc)) {
                classOption.webrtcType = Number(options.webrtc)
            }
            this.webrtc = classOption.webrtcType

            console.log('[bjy-live] room init options', {
                env: options.env,
                prefixName: options.prefixName,
                room_id: options.room_id,
                user_number: options.user_number,
                user_name: options.user_name,
                user_role: options.user_role,
                teacher_number: options.teacher_number || options.user_number,
                teacher_role: options.teacher_role != null ? options.teacher_role : options.user_role,
                webrtc: options.webrtc,
                sign: options.sign,
                live_thumb: options.live_thumb,
                live_time: options.live_time
            })

            BJY.init({
                env: options.env,
                privateDomainPrefix: options.prefixName,
                class: classOption,
                user: {
                    number: String(options.user_number),
                    avatar: decodeURIComponent(options.user_avatar),
                    name: decodeURIComponent(options.user_name),
                    type: Number(options.user_role)
                },
                teacher: {
                    number: String(options.teacher_number || options.user_number),
                    avatar: decodeURIComponent(options.teacher_avatar || options.user_avatar),
                    name: decodeURIComponent(options.teacher_name || options.user_name),
                    type: Number(options.teacher_role != null ? options.teacher_role : options.user_role)
                },
                sign: options.sign
            })
        },
        normalizeRoomOptions(source) {
            const hasOwn = (key) => Object.prototype.hasOwnProperty.call(source || {}, key)
            return {
                env: source.env || 'production',
                prefixName: source.prefixName || source.prefix_name || source.privateDomainPrefix || source.private_domain_prefix,
                room_id: source.room_id || source.roomId || source.id,
                user_number: source.user_number || source.userNumber || source.number || '0',
                user_avatar: source.user_avatar || source.userAvatar || source.avatar || '',
                user_name: source.user_name || source.userName || source.name || sessionStorage.getItem('access-nick') || '学员',
                user_role: hasOwn('user_role') ? source.user_role : hasOwn('userRole') ? source.userRole : 0,
                teacher_number: source.teacher_number || source.teacherNumber,
                teacher_avatar: source.teacher_avatar || source.teacherAvatar || '',
                teacher_name: source.teacher_name || source.teacherName || '老师',
                teacher_role: hasOwn('teacher_role') ? source.teacher_role : hasOwn('teacherRole') ? source.teacherRole : 1,
                sign: source.sign,
                webrtc: hasOwn('webrtc') ? source.webrtc : 1,
                live_thumb: source.live_thumb || '',
                live_time: source.live_time || ''
            }
        },
        handleClassStart() {
            eventEmitter.trigger(eventEmitter.CLASS_START_TRIGGER)
        },
        formatStartTime(value) {
            const date = value instanceof Date ? value : new Date(value)
            if (isNaN(date.getTime())) {
                return ''
            }
            const y = date.getFullYear()
            const m = String(date.getMonth() + 1).padStart(2, '0')
            const d = String(date.getDate()).padStart(2, '0')
            const h = String(date.getHours()).padStart(2, '0')
            const min = String(date.getMinutes()).padStart(2, '0')
            return `${y}-${m}-${d} ${h}:${min}`
        },
        startCountdown(value) {
            this.clearCountdown()
            const target = value instanceof Date ? value : new Date(value)
            if (isNaN(target.getTime())) {
                return
            }
            const tick = () => {
                const now = Date.now()
                const diff = target.getTime() - now
                if (diff <= 0) {
                    this.countdownText = ''
                    this.clearCountdown()
                    return
                }
                const days = Math.floor(diff / (1000 * 60 * 60 * 24))
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
                const seconds = Math.floor((diff % (1000 * 60)) / 1000)
                if (days > 0) {
                    this.countdownText = `${days}天 ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
                } else {
                    this.countdownText = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
                }
            }
            tick()
            this.countdownTimer = setInterval(tick, 1000)
        },
        clearCountdown() {
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer)
                this.countdownTimer = null
            }
            this.countdownText = ''
        },
        initPosterInfo() {
            const options = this.roomOptions || {}
            console.log('[bjy-live] initPosterInfo roomOptions:', options)
            const startTime = options.live_time || ''
            this.liveThumb = options.live_thumb || ''
            console.log('[bjy-live] live_thumb:', this.liveThumb, 'live_time:', startTime)
            if (startTime) {
                this.liveStartTime = this.formatStartTime(startTime)
                this.startCountdown(startTime)
            } else {
                this.liveStartTime = ''
                this.clearCountdown()
            }
        },
        lockViewport() {
            const meta = document.querySelector('meta[name="viewport"]')
            if (meta) {
                this.originalViewport = meta.content
                meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
            }
        },
        unlockViewport() {
            const meta = document.querySelector('meta[name="viewport"]')
            if (meta && this.originalViewport) {
                meta.content = this.originalViewport
            }
        },
        preventGesture(e) {
            e.preventDefault()
        },
        preventDblClick(e) {
            e.preventDefault()
        },
        bindZoomPrevent() {
            document.addEventListener('gesturestart', this.preventGesture, { passive: false })
            document.addEventListener('dblclick', this.preventDblClick, { passive: false })
        },
        unbindZoomPrevent() {
            document.removeEventListener('gesturestart', this.preventGesture)
            document.removeEventListener('dblclick', this.preventDblClick)
        },
        bindFullscreenState() {
            document.addEventListener('fullscreenchange', this.handleFullscreenChange)
            document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
            document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
            document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)
        },
        unbindFullscreenState() {
            document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
            document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
            document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange)
            document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange)
        },
        handleFullscreenChange() {
            const isFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            )
            const layout = this.$el.querySelector('.simple-layout')
            if (layout) {
                layout.classList.toggle('is-fullscreen', isFullscreen)
            }
        },
    },
    beforeDestroy() {
        $('body').removeClass('student')
        window.onmessage = null
        eventEmitter.off(this.namespace)
        BJY.dispose(BJY.config.BJY_DISPOSE_TYPE_OUT_ROOM)
        this.loaded = false
        this.unlockViewport()
        this.unbindZoomPrevent()
        this.unbindFullscreenState()
    }
}
</script>

<style lang="scss">
    @import './assets/iconfont/iconfont.css';

    #app {
        font-family: 'Avenir', Helvetica, Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        height: 100%;
        width: 100%;

        #main {
            position: absolute;
            top: 0;
            width: 100%;
            height: 100%;
            opacity: 0;

            .simple-layout {
                display: flex;
                width: 100%;
                height: 100%;

                .live-area {
                    flex: 1;
                    min-width: 0;
                    height: 100%;
                    position: relative;
                    background-image: url('./assets/img/class-panel-bg.jpg');

                    .live-poster {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        z-index: 3;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(0, 0, 0, 0.6);

                        .poster-bg {
                            position: absolute;
                            top: 0;
                            left: 0;
                            right: 0;
                            bottom: 0;
                            background-image: url('/images/live-poster.jpg');
                            background-size: cover;
                            background-position: center;
                            opacity: 0.4;

                            &[style*="background-image"] {
                                opacity: 0.6;
                            }
                        }

                        .poster-info {
                            position: relative;
                            z-index: 1;
                            text-align: center;
                            color: #fff;
                            padding: 20px;
                            background: #363434cc;
                            border-radius: 10px;

                            .poster-title {
                                font-size: 20px;
                                font-weight: 500;
                                margin-bottom: 12px;
                            }

                            .poster-time {
                                font-size: 14px;
                                color: rgba(255, 255, 255, 0.8);
                                margin-bottom: 8px;
                            }

                            .poster-countdown {
                                font-size: 14px;
                                color: #1795ff;
                            }
                        }
                    }

                    .start-btn {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        background: #1795ff;
                        border-radius: 5px;
                        width: 100px;
                        height: 50px;
                        line-height: 50px;
                        color: #fff;
                        text-align: center;
                        font-size: 22px;
                        cursor: pointer;
                        z-index: 10;
                    }


                }

                .chat-area {
                    width: 320px;
                    height: 100%;
                    position: relative;
                    background: #fff;
                    border-left: 1px solid #ddd;
                    box-shadow: -2px 0 6px rgba(0, 0, 0, 0.1);
                    z-index: 1;
                    display: flex;
                    flex-direction: column;
                    padding: 0 10px;

                    .chat-tabs {
                        flex: none;
                        display: flex;
                        height: 44px;
                        border-bottom: 1px solid #ddd;
                        background: #f8f9fa;

                        .chat-tab {
                            flex: 1;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 14px;
                            color: #666;
                            cursor: pointer;
                            position: relative;

                            &.active {
                                color: #1795ff;
                                font-weight: 500;

                                &::after {
                                    content: '';
                                    position: absolute;
                                    bottom: 0;
                                    left: 20%;
                                    right: 20%;
                                    height: 2px;
                                    background: #1795ff;
                                    border-radius: 1px;
                                }
                            }
                        }
                    }

                    .chat-tab-content {
                        flex: 1;
                        min-height: 0;
                        position: relative;

                        .user-list-panel,
                        .message-panel {
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            overflow: hidden;
                        }
                    }
                }
            }

            @media screen and (max-width: 768px) {
                .simple-layout {
                    flex-direction: column;

                    .live-area {
                        flex: none;
                        width: 100%;
                        height: 40vh;
                        background-size: cover;
                    }

                    .chat-area {
                        flex: 1;
                        width: 100%;
                        border-left: none;
                        border-top: 1px solid #ddd;
                        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
                    }
                }

                .simple-layout.is-fullscreen {
                    .live-area {
                        height: 100vh !important;
                    }
                }
            }

            .simple-layout.is-fullscreen {
                .live-area {
                    flex: none !important;
                    width: 100% !important;
                    height: 100% !important;
                }

                .chat-area {
                    display: none !important;
                }
            }
        }
    }

    #survey-container {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
        iframe {
            width: 100%;
            height: 100%;
            border: none;
        }
    }

    // 隐藏播放器左上角 logo 水印
    .bjy-player-theme-default .bjy-watermark.left-top {
        display: none !important;
    }

    // 移动端播放器控制栏优化：避免点击两次才全屏、退出按钮被遮挡、声音按钮无反应
    @media screen and (max-width: 768px) {
        .bjy-player-theme-default.bjy-simple .bjy-footer {
            opacity: 1 !important;
        }

        .bjy-player-theme-default.bjy-simple .bjy-footer .bjy-right {
            display: flex !important;
            align-items: center;
            bottom: 40px;
            right: 12px;
            z-index: 10;
        }

        .bjy-player-theme-default .bjy-icon.bjy-enter-fullscreen,
        .bjy-player-theme-default .bjy-icon.bjy-exit-fullscreen {
            width: 24px !important;
            height: 24px !important;
            background-size: 24px 24px !important;
            background-position: center !important;
            background-repeat: no-repeat !important;
            cursor: pointer;
        }

        // Element Plus 风格的线框全屏/退出全屏图标
        .bjy-player-theme-default .bjy-icon.bjy-enter-fullscreen {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4'/%3E%3C/svg%3E") !important;
        }

        .bjy-player-theme-default .bjy-icon.bjy-exit-fullscreen {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M8 4v4H4M16 4v4h4M8 20v-4H4M16 20v-4h4'/%3E%3C/svg%3E") !important;
        }

        .bjy-player-theme-default .bjy-icon.bjy-enter-fullscreen:hover,
        .bjy-player-theme-default .bjy-icon.bjy-exit-fullscreen:hover {
            opacity: 0.8;
        }

        // 全屏状态下：老师姓名宽度缩小，全屏按钮居中并距右 20px
        .bjy-player-theme-default[data-fullscreen='1'] .bjy-footer-name {
            margin-left: 10px !important;
            max-width: 120px !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
        }

        .bjy-player-theme-default[data-fullscreen='1'] .bjy-footer .bjy-right {
            top: 50% !important;
            right: 20px !important;
            bottom: auto !important;
            transform: translateY(-50%) !important;
        }

        // 隐藏"只听声音"按钮（移动端 iOS 下无法控制音量）
        .bjy-player-theme-default .bjy-icon.bjy-toggle {
            display: none !important;
        }
    }
</style>
